/**
 * CashPositionCard - Component for displaying cash positions by currency
 * Shows cash holdings with conversion to base currency
 */

import React from 'react';
import { Wallet } from 'lucide-react';

export interface CashPosition {
  currency: string;
  amount: number;
  amount_base?: number | null;  // TASK-865: server-derived; null = no rate
  base_currency?: string;
  exchange_rate?: number;
  percentage?: number;
}

export interface CashPositionCardProps {
  position: CashPosition;
  baseCurrency?: string;
  showExchangeRate?: boolean;
  showPercentage?: boolean;
  className?: string;
  onClick?: () => void;
}

const CashPositionCard: React.FC<CashPositionCardProps> = ({
  position,
  baseCurrency = 'CHF',
  showExchangeRate = true,
  showPercentage = false,
  className = '',
  onClick
}) => {
  const formatCurrency = (value: number, currency: string) => {
    return new Intl.NumberFormat('de-CH', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const isBaseCurrency = position.currency === baseCurrency;

  return (
    <div 
      className={`
        bg-white border border-gray-200 rounded-lg p-4
        ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}
        ${className}
      `}
      onClick={onClick}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <Wallet className="w-5 h-5 text-gray-400" />
          <span className="text-lg font-semibold text-gray-900">
            {position.currency}
          </span>
        </div>
        {showPercentage && position.percentage && (
          <span className="text-sm text-gray-500">
            {position.percentage.toFixed(1)}%
          </span>
        )}
      </div>

      <div className="space-y-2">
        <div>
          <p className="text-xs text-gray-500">Amount</p>
          <p className="text-lg font-bold text-gray-900">
            {formatCurrency(position.amount, position.currency)}
          </p>
        </div>

        {!isBaseCurrency && position.amount_base != null && (
          <div>
            <p className="text-xs text-gray-500">Value in {baseCurrency}</p>
            <p className="text-base font-medium text-gray-700">
              {formatCurrency(position.amount_base, baseCurrency)}
            </p>
          </div>
        )}

        {/* TASK-865: a balance this platform cannot price says so, rather than being
            rendered at par or silently omitted. */}
        {!isBaseCurrency && position.amount_base == null && (
          <div>
            <p className="text-xs text-gray-500">Value in {baseCurrency}</p>
            <p className="text-base font-medium text-amber-700">
              No {baseCurrency} rate for {position.currency}
            </p>
          </div>
        )}

        {showExchangeRate && !isBaseCurrency && position.exchange_rate && (
          <div className="pt-2 border-t border-gray-100">
            <div className="flex items-center justify-between text-xs">
              <span className="text-gray-500">Exchange Rate</span>
              <span className="font-medium text-gray-700">
                1 {position.currency} = {position.exchange_rate.toFixed(4)} {baseCurrency}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CashPositionCard;